import '@fontsource/barlow-condensed/latin-500.css';
import '@fontsource/barlow-condensed/latin-700.css';
import '@fontsource/barlow-condensed/latin-800-italic.css';
import './ui/style.css';
import './ui/menus.css';
import './ui/touch.css';
import * as THREE from 'three/webgpu';
import { GAME_TITLE, GAME_TAGLINE, BEST_TIMES_KEY, GHOST_KEY } from './config';
import { createRenderer, graphicsUnavailable, type RendererInfo } from './render/renderer';
import { InputDevices } from './input/devices';
import { DriveSession } from './game/session';
import { getCar, getStage, visibleCars, visibleStages, CARS, STAGES } from './content/registry';
import { DebugOverlay } from './ui/debug';
import { StageDriver, type BotKind } from './bots/stage-bots';
import { gripFromCalibration } from './codriver/calibration-table';
import { AudioMixer } from './audio/mixer';
import { CodriverVoice } from './codriver/voice';
import { CarAudio } from './audio/car-audio';
import { CodriverRuntime, DEFAULT_CODRIVER } from './codriver/runtime';
import type { StageNotes } from './codriver/notes';
import { PaceNoteHud } from './ui/pacenotes';
import { loadGameTextures } from './render/textures';
import { PRESETS, detectPreset, type PresetName, type QualitySettings } from './render/quality';
import { Benchmark } from './game/benchmark';
import { GcGuard, HEAVY_PRESET_EXTRA_MB } from './game/gc-guard';
import { localSettingsStore, qualityFor } from './game/settings';
import { bestTimesStore } from './game/timing';
import { ghostStore } from './game/ghost';
import { GhostView } from './render/ghost-view';
import { CarWear } from './render/car-wear';
import { GameFlow, stageLayout } from './game/flow';
import { Hud } from './ui/hud';
import { Screens } from './ui/screens';
import { TouchControls } from './ui/touch';

const params = new URLSearchParams(location.search);
const DEBUG = params.has('debug');
/** §14 benchmark: fixed DPR 1, no dynamic resolution, no pause on blur, safe bot from the start. */
const BENCH = params.has('benchmark');
/** Dev tools: skip the menu and countdown (?autopilot=<bot> or ?drive). */
const DIRECT = BENCH || params.has('autopilot') || params.has('drive');

const TIPS = [
  'W / ↑ throttle · S / ↓ brake · A D / ← → steer · Space handbrake.',
  'On gravel the car slides: steer with the throttle as much as the wheel.',
  'Listen to the co-driver: "four left" is faster than "two left".',
  'R puts you back on the road — with a time penalty.',
  'C changes camera. The cockpit view is the real thing.',
];

function setProgress(f: number, status: string): void {
  const fill = document.getElementById('loading-fill');
  const st = document.getElementById('loading-status');
  if (fill) fill.style.width = `${Math.round(f * 100)}%`;
  if (st) st.textContent = status;
}

function fatal(title: string, msg: string): void {
  const el = document.createElement('div');
  el.className = 'fatal';
  el.innerHTML = `<div><h1></h1><p></p><button class="btn btn-primary" type="button"><span>RELOAD</span></button></div>`;
  el.querySelector('h1')!.textContent = title;
  el.querySelector('p')!.textContent = msg;
  const b = el.querySelector('button')!;
  b.addEventListener('click', () => location.reload());
  setTimeout(() => b.focus(), 0);
  document.body.appendChild(el);
}

declare global {
  interface Window {
    __game?: {
      ready: boolean;
      backend: string;
      gpu: string;
      buildId: string;
      session: DriveSession | null;
      errors: string[];
      audio: { state: string; voice: string; fallback: string };
      codriver: CodriverRuntime | null;
      quality: string;
      /** Audio graph root (tests and mix checks tap the master bus). */
      mixer: AudioMixer | null;
      /** Car sound (tests read `granular`: the recorded engine bank is playing). */
      carAudio?: CarAudio | null;
      flow: GameFlow | null;
      /** Dirt and damage (dev tools and tests). */
      wear: CarWear | null;
      gc?: { reservedMb: number; enabled: boolean; checkpoints: number; collected: number };
      /** The running benchmark (?benchmark), for tools. */
      bench?: Benchmark | null;
    };
  }
}

async function boot(): Promise<void> {
  window.__game = { ready: false, backend: 'none', gpu: '', buildId: __BUILD_ID__, session: null, errors: [], audio: { state: 'locked', voice: 'none', fallback: '' }, codriver: null, quality: '', mixer: null, flow: null, wear: null };
  // GC headroom before anything else is allocated (src/game/gc-guard.ts).
  const gc = new GcGuard({ reserve: GcGuard.wantsReserve() });
  const gcInfo = { reservedMb: gc.reservedMb, enabled: gc.enabled, checkpoints: 0, collected: 0 };
  window.__game.gc = gcInfo;
  document.title = `${GAME_TITLE} — forest rally stage`;
  const tip = document.getElementById('loading-tip');
  if (tip) tip.textContent = TIPS[Math.floor(Math.random() * TIPS.length)];

  if (graphicsUnavailable()) {
    fatal('Graphics not supported', 'This browser has neither WebGPU nor WebGL2. Try a current Chrome, Edge, Firefox or Safari on a desktop computer.');
    return;
  }
  setProgress(0.05, 'Starting renderer');
  const viewport = document.getElementById('viewport')!;
  const canvas = document.createElement('canvas');
  viewport.appendChild(canvas);
  let info: RendererInfo;
  try {
    // GPU timestamps only where they are read: the benchmark (unless ?gputime=0) and ?debug.
    const gpuTimestamps = (BENCH && params.get('gputime') !== '0') || DEBUG;
    info = await createRenderer(canvas, (m) => fatal('Graphics device lost', m), gpuTimestamps);
  } catch (e) {
    console.error(e);
    fatal('Could not start graphics', String((e as Error)?.message ?? e));
    return;
  }
  const renderer = info.renderer;
  renderer.setSize(window.innerWidth, window.innerHeight);
  const tm = params.get('tm');
  renderer.toneMapping = tm === 'agx' ? THREE.AgXToneMapping : tm === 'neutral' ? THREE.NeutralToneMapping : THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.0;
  renderer.shadowMap.enabled = true;
  window.__game.backend = info.backend;
  window.__game.gpu = info.gpuName;

  // Graphics: the player's settings (auto-detected preset by default); URL parameters override them
  // for the benchmark and dev tools (?quality=, ?nodynres, ?shadows=).
  const store = localSettingsStore();
  const settings = store.load();
  const touchOnly = matchMedia('(pointer: coarse)').matches && !matchMedia('(pointer: fine)').matches;
  const detected = detectPreset(info.gpuName, info.backend, touchOnly);
  const qParam = params.get('quality') as PresetName | null;
  const qualityOverride = (q: QualitySettings): QualitySettings => {
    let r = qParam && qParam in PRESETS ? { ...PRESETS[qParam] } : q;
    if (params.has('nodynres') || BENCH) r = { ...r, dynamicResolution: false };
    if (params.has('shadows')) r = { ...r, shadowCascades: Number(params.get('shadows')) };
    if (params.has('grass')) r = { ...r, grassRadius: Number(params.get('grass')) };
    // Phones (touch-only): sharper on high-DPI screens than Low's 1× cap, lighter particles and
    // vegetation; dynamic resolution backs the render scale off if the phone can't keep up.
    if (touchOnly && !qParam) r = { ...r, maxPixelRatio: Math.max(r.maxPixelRatio, 1.5), particles: Math.min(r.particles, 0.4), vegetation: Math.min(r.vegetation, 0.5) };
    return r;
  };
  const applyPixelRatio = (q: QualitySettings): void => {
    renderer.setPixelRatio(BENCH ? 1 : Math.min(window.devicePixelRatio, q.maxPixelRatio));
    renderer.setSize(window.innerWidth, window.innerHeight);
    window.__game!.quality = q.preset;
  };
  const quality = qualityOverride(qualityFor(settings.graphics, detected));
  applyPixelRatio(quality);
  const presetName = quality.preset;
  // High and Ultra render six passes a frame: more GC headroom for a whole stage (src/game/gc-guard.ts).
  if (presetName === 'high' || presetName === 'ultra') {
    gc.topUp(HEAVY_PRESET_EXTRA_MB);
    gcInfo.reservedMb = gc.reservedMb;
  }

  setProgress(0.12, 'Loading textures');
  const textures = await loadGameTextures(Math.min(8, renderer.getMaxAnisotropy()), (f) => setProgress(0.12 + 0.3 * f, 'Loading textures'));

  setProgress(0.45, 'Building stage');
  const devices = new InputDevices();
  // URL (dev, tests, hidden content) → the player's remembered choice → the first visible entry.
  const pick = (param: string | null, saved: string, all: { id: string }[], visible: { id: string }[]) =>
    param && all.some((x) => x.id === param) ? param : visible.some((x) => x.id === saved) ? saved : visible[0].id;
  const stageId = pick(params.get('stage'), settings.stage, STAGES, visibleStages());
  const carId = pick(params.get('car'), settings.car, CARS, visibleCars());
  const session = await DriveSession.create({
    renderer,
    car: getCar(carId).spec,
    modelUrl: getCar(carId).modelUrl,
    stage: getStage(stageId).spec,
    assists: { ...settings.assists },
    devices,
    textures,
    quality,
  });
  window.__game.session = session;
  // Dirt and damage hook into the car's materials before anything is compiled.
  const wear = session.car.model ? CarWear.attach(session.car.model) : null;
  window.__game.wear = wear;
  session.world.buildEnvironment(renderer);
  session.world.bakeBillboards(renderer);
  const auto = (BENCH ? 'safe' : params.get('autopilot')) as BotKind | null;
  // ?bot=<kind> (dev/test): the menu, start line and lights as for a player, then the bot drives.
  const botKind = params.get('bot') as BotKind | null;
  const testBot = botKind && session.stage.track && session.stage.index ? ((d) => (dt: number) => d.update(session.sim.vehicle, dt))(new StageDriver(session.stage.track, session.stage.index, botKind, gripFromCalibration())) : null;
  let bench: Benchmark | null = null;
  if (auto && session.stage.track && session.stage.index) {
    const driver = new StageDriver(session.stage.track, session.stage.index, auto, gripFromCalibration());
    // Brake below the 0.5 that selects reverse at a standstill (or a held car backs away), plus the
    // handbrake.
    const hold = { steer: 0, throttle: 0, brake: 0.4, handbrake: 1, shiftUp: false, shiftDown: false };
    // ?autopilot=<bot>&settle=<s> (dev): wait on the grid first, as the benchmark does.
    const settle = bench ? 0 : Number(params.get('settle') ?? 0);
    session.autopilot = (dt) => (bench?.settling || session.simTime < settle ? hold : driver.update(session.sim.vehicle, dt));
  }

  // Audio starts on the first user gesture; the voice sprite downloads in the background.
  const mixer = new AudioMixer();
  const voice = new CodriverVoice(mixer);
  void voice.load();
  window.__game.mixer = mixer;
  const carAudio = new CarAudio(mixer);
  window.__game.carAudio = carAudio;
  mixer.onReady((ctx) => void carAudio.init(ctx));
  carAudio.setCrowds(session.world.crowds?.sites ?? []);
  const unlock = (): void => {
    if (!mixer.unlock() && mixer.failed) voice.noAudio();
  };
  for (const ev of ['pointerdown', 'keydown', 'touchstart'] as const) window.addEventListener(ev, unlock, { capture: true });
  const stageEntry = getStage(stageId);
  const ui = document.getElementById('ui')!;
  let codriver: CodriverRuntime | null = null;
  if (stageEntry.notes && stageEntry.spec.kind === 'road') {
    const hud = new PaceNoteHud(ui);
    codriver = new CodriverRuntime(stageEntry.notes as StageNotes, stageEntry.spec.startDistance, voice, hud, { ...DEFAULT_CODRIVER, ...settings.codriver });
    // The co-driver figure's tablet shows the same notes as the strip (ui/tablet-notes.ts).
    session.car.codriver?.setCue({ notes: () => hud.visible, version: () => hud.version, speaking: () => hud.speaking });
  }
  window.__game.codriver = codriver;

  const carEntry = getCar(carId);
  const st = stageEntry.spec;
  const hud = new Hud(ui, stageLayout(st), carEntry.spec.engine.limiterRpm);
  const screens = new Screens(ui);
  // Ghost car, added before the loading-screen warm-up so its material is built up front.
  const ghostView = st.kind === 'road' ? new GhostView(session.car, carEntry.spec) : null;
  if (ghostView) session.world.scene.add(ghostView.root);
  // Touch controls feed the same raw-controls path as keys and pads.
  const touch = new TouchControls(ui, { pause: () => flow.pause(), camera: () => session.rig.cycle(), reset: () => flow.requestReset(false) }, touchOnly);
  devices.touch = touch.raw;
  const flow = new GameFlow({
    touch,
    session,
    devices,
    mixer,
    carAudio,
    codriver,
    hud,
    screens,
    ui,
    store,
    settings,
    detectedPreset: detected,
    qualityOverride,
    onQuality: applyPixelRatio,
    best: bestTimesStore(BEST_TIMES_KEY),
    ghosts: ghostStore(GHOST_KEY),
    ghostView,
    wear,
    stageId,
    carId,
    stageName: st.name,
    carName: carEntry.spec.name,
    cars: visibleCars().map((c) => ({ id: c.id, name: c.spec.name })),
    stages: visibleStages().map((s) => ({ id: s.id, name: s.spec.name })),
    carLine: '1.6 L turbo · 32 mm restrictor · 4WD · 5-speed sequential',
    buildId: __BUILD_ID__,
    title: GAME_TITLE,
    tagline: GAME_TAGLINE,
    touchOnly,
    bench: BENCH,
    gc,
    testBot: testBot ?? undefined,
  });
  window.__game.flow = flow;

  setProgress(0.8, 'Compiling shaders');
  if (!params.has('nocompile')) await renderer.compileAsync(session.world.scene, session.rig.camera);
  setProgress(1, 'Ready');

  const debug = DEBUG ? new DebugOverlay(ui) : null;
  if (debug) {
    debug.extra = () =>
      `backend ${info.backend}  gpu ${info.gpuName}\nsteps/frame ${session.stepsLastFrame}  sim ${session.simMsLastFrame.toFixed(2)} ms\n` +
      `preset ${presetName}  scale ${session.dynRes.scale.toFixed(3)}  instances ${session.world.foliage.visibleCount}\n` +
      `stage s ${session.progressS.toFixed(0)} m  d ${session.lateral.toFixed(1)} m\naudio ${mixer.ctx?.state ?? 'locked'}  voice ${voice.mode}${voice.fallbackReason ? ` (${voice.fallbackReason})` : ''}`;
  }

  window.addEventListener('resize', () => {
    renderer.setSize(window.innerWidth, window.innerHeight);
    session.resize(window.innerWidth, window.innerHeight);
  });

  if (DIRECT || st.kind !== 'road') {
    flow.startDirect();
    const camParam = params.get('cam');
    if (camParam) session.rig.setMode(camParam as Parameters<typeof session.rig.setMode>[0]);
  } else flow.enterMenu();

  bench = BENCH ? new Benchmark(session, renderer, { preset: presetName, backend: info.backend, gpu: info.gpuName, timestamps: info.timestamps }, 4, Number(params.get('seconds') ?? 70), Number(params.get('settle') ?? 12), Number(params.get('settleNav') ?? 33) * 1000, gc) : null;
  window.__game.bench = bench;
  let last = performance.now();
  let frames = 0;
  setProgress(0.95, 'Preparing the stage');
  renderer.setAnimationLoop(() => {
    const now = performance.now();
    if (bench?.pace(now)) return;
    // The first few frames build the remaining GPU pipelines (≈1.5 s on the Radeon 780M): keep them
    // behind the loading screen.
    const dt = frames < 2 ? 0 : (now - last) / 1000;
    last = now;
    // Warm-up from frame 1: the first frame's foliage rebuild re-hides the inactive ping-pong copies.
    if (frames === 1) session.world.warmup(true);
    if (frames === 4) session.world.warmup(false);
    flow.frame(dt, now);
    if (++frames === 10) {
      document.getElementById('loading')?.classList.add('done');
      window.__game!.ready = true;
    }
    bench?.sample(dt, flow.logicMs);
    gcInfo.checkpoints = gc.checkpoints;
    gcInfo.collected = gc.collected;
    const a = window.__game!.audio;
    a.state = mixer.failed ? 'failed' : (mixer.ctx?.state ?? 'locked');
    a.voice = voice.mode;
    a.fallback = voice.fallbackReason;
    debug?.update(session.sim.vehicle, now);
  });
}

window.addEventListener('error', (e) => window.__game?.errors.push(String(e.message)));
window.addEventListener('unhandledrejection', (e) => window.__game?.errors.push(String((e as PromiseRejectionEvent).reason)));
boot().catch((e) => {
  console.error(e);
  fatal('Something went wrong', String((e as Error)?.message ?? e));
});
